import { useToken } from "@gluestack-style/react";
import abcoLogo from "assets/abco-logo.png";
import { useUser } from "components/clerk";
import EnquireForm from "components/form/EnquireForm";
import Breadcrumbs from "components/general/Breadcrumbs";
import Container from "components/general/Container";
import InfoBox from "components/general/InfoBox";
import PageLoader from "components/general/PageLoader";
import ProductGallery from "components/general/ProductGallery";
import ScrollContainer from "components/general/ScrollContainer";
import Seo from "components/general/Seo";
import { Tab, TabContainer } from "components/general/Tabs";
import AddToCart from "components/item/AddToCart";
import { ItemAlternativeButton } from "components/item/ItemAlternativeButton";
import ItemDownload from "components/item/ItemDownload";
import ItemListItem from "components/item/ItemListItem";
import ItemStatusBadge from "components/item/ItemStatusBadge";
import { Box, Button, FormattedCurrency, H1, H3, H4, HStack, Image, ParagraphSmall, Text, Typography, VStack } from "components/ui";
import { useRouter } from "expo-router";
import { BookCheck, X } from "lucide-react-native";
import React, { useEffect, useState } from "react";
import Modal from "react-native-modal/dist/modal";
import RenderHtml from "react-native-render-html";
import { Item, useGetCartCurrentQuery, usePostItemFindMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

const tagsStyles = {
	p: {
		fontSize: 15,
		lineHeight: 22,
		marginBottom: 12,
	},
	ul: {
		marginLeft: 20,
		marginBottom: 12,
	},
	li: {
		fontSize: 15,
		lineHeight: 22,
		marginBottom: 6,
		fontFamily: "Roboto_400Regular",
	},
	h1: { fontSize: 22, fontWeight: "bold" as const, marginVertical: 10 },
	h2: { fontSize: 18, fontWeight: "bold" as const, marginVertical: 8 },
	h4: { fontSize: 16, fontWeight: "bold" as const, marginVertical: 6 },
	strong: {
		fontWeight: "bold" as const,
	},
	div: {
		paddingVertical: 10,
	},
};

export default function ItemListing({ item }: { item: Item }) {
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const [isOpenEnquireNow, setIsOpenEnquireNow] = useState(false);
	const isMobile = useIsMobile();
	const { data: cart, isSuccess } = useGetCartCurrentQuery();
	const { isSignedIn } = useUser();
	const defaultTextProps = {
		selectable: true,
	};
	const router = useRouter();
	var fileCollection = item.fileCollection === null ? [] : JSON.parse(item.fileCollection || "[]");
	if (item?.imageCollection?.length > 0 && item.imageUrl) {
		// Create a new array to avoid mutating the original
		const updatedImageCollection = [item.imageUrl, ...item.imageCollection];
		item = { ...item, imageCollection: updatedImageCollection };
	}
	return (
		<ScrollContainer>
			<Seo title={item.name} description={item.description} />
			<Box bg="$neutral25">
				<Container>
					{!isMobile && (
						<Box bg="$basicwhite" borderRadius={12} padding={24}>
							<Breadcrumbs hierarchy={item.categoryCollection} />
						</Box>
					)}

					<Box padding={isMobile ? 0 : 48}>
						<Box flex={1} flexDirection={isMobile ? "column" : "row"} gap={28}>
							<Box flex={1}>
								<Box padding={12} borderRadius={isMobile ? 0 : 16} bg="$basicwhite" justifyContent="center" alignItems="center">
									{item?.imageCollection?.length > 0 ? (
										<ProductGallery data={item} />
									) : item.imageUrl ? (
										<Image aspectRatio={1.32 / 1} resizeMode="contain" borderRadius={20} width={400} source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + item.imageUrl + "?width=400&quality=75&format=webp"} alt="" />
									) : (
										<Image
											aspectRatio={1}
											w="$full"
											source={abcoLogo}
											borderTopLeftRadius={isMobile ? 8 : 20}
											borderTopRightRadius={isMobile ? 8 : 20}
											alignSelf="center"
											// @ts-ignore
											style={{
												width: "80%",
												height: "100%",
												maxHeight: 280,
												objectFit: "cover",
												opacity: 0.3,
												filter: "grayscale(100%)",
												transition: "transform 0.3s ease",
											}}
											alt=""
											resizeMode="contain"
										/>
									)}
								</Box>
							</Box>
							<Box paddingHorizontal={isMobile ? 16 : 0} gap={isMobile ? 24 : 36} w={isMobile ? "$full" : "$1/3"}>
								<Box>
									<ParagraphSmall sx={{ marginBottom: 4 }} weight="normal">
										CODE: {item.stockcode}
									</ParagraphSmall>
									<H1 sx={{ paddingBottom: isMobile ? 0 : 16 }}>{item.name}</H1>

									<HStack alignItems="flex-end" gap={8} paddingVertical={16}>
										<FormattedCurrency sx={{ fontSize: isMobile ? 22 : 36, lineHeight: isMobile ? 24 : 32, fontWeight: "bold" }} amount={item.unitPrice} />
										{typeof item.unitPrice === "number" && typeof item.tradePrice === "number" && item.unitPrice < item.tradePrice && (
											<FormattedCurrency sx={{ color: "$neutral600", fontSize: isMobile ? 16 : 20, textDecorationLine: "line-through", fontWeight: "bold" }} amount={item.tradePrice} />
										)}
										<ParagraphSmall weight="normal" sx={{ color: "$neutral600" }}>
											Excl. GST
										</ParagraphSmall>
									</HStack>
								</Box>
								{!isMobile && item.isPurchaseable && (
									<Box maxWidth={232}>
										{!isSignedIn && item.unitPrice && item.unitPrice > 3500 ? (
											<>
												<Button onPress={setIsOpenEnquireNow} variant="secondary" width="$full">
													<Typography weight="bold">Enquire Now</Typography>
												</Button>
												<Modal
													animationIn={"fadeIn"}
													animationOut={"fadeOut"}
													scrollOffset={1}
													onBackdropPress={() => setIsOpenEnquireNow(false)}
													isVisible={isOpenEnquireNow}
													style={{ maxWidth: 1240, marginLeft: "auto", marginRight: "auto", width: "100%" }}
												>
													<Box bg="$basicwhite" borderRadius={12}>
														<HStack alignItems="center" justifyContent="space-between" bg="$lighttealxlight" borderTopLeftRadius={12} borderTopRightRadius={12} paddingHorizontal={28} paddingVertical={20}>
															<H3>Enquire Now</H3>
															<Box>
																<Button onPress={() => setIsOpenEnquireNow(false)} gap={6}>
																	<X />
																	<Typography>Close</Typography>
																</Button>
															</Box>
														</HStack>
														<Box padding={24}>
															<EnquireForm setIsOpenEnquireNow={setIsOpenEnquireNow} />
														</Box>
													</Box>
												</Modal>
												{/* <PopupModal title="Enquire Now">
													<EnquireForm />
												</PopupModal> */}
											</>
										) : (
											<AddToCart stockcode={item.stockcode} isPurchaseable={item.isPurchaseable} item={item} />
										)}
									</Box>
								)}

								{!isMobile && !item.isPurchaseable && (
									<Box maxWidth={"auto"}>
										<Typography weight="normal">Please contact your supervisor to request this item.</Typography>
									</Box>
								)}

								<VStack gap={12} bg="$basicwhite" borderRadius={12} paddingVertical={12} paddingHorizontal={20}>
									<Box alignItems="center" gap={12} flexDirection="row">
										{cart && cart.locationId && item.itemAvailability && item.itemAvailability.find((loc) => loc.locationId === cart.locationId) ? (
											<Text alignSelf="center" fontWeight={"bold"}>
												{item.itemAvailability.find((loc) => loc.locationId === cart.locationId)?.locationName}
											</Text>
										) : null}
										<ItemStatusBadge showOnlyStockStatus size={12} item={item} />
										<ItemStatusBadge showOnlyOtherLocations size={12} item={item} />
									</Box>

									{cart &&
									cart.locationId &&
									item.itemAvailability &&
									(() => {
										const matchingLocation = item.itemAvailability.find((loc) => loc.locationId === cart.locationId);
										return matchingLocation && matchingLocation.status !== "InStock";
									})() ? (
										<InfoBox text="This product is in stock for other locations. Adding this product to your order may be dispatched in multiple deliveries" />
									) : null}
									<ItemAlternativeButton item={item} />
								</VStack>
							</Box>
						</Box>
						{isMobile && item.isPurchaseable && (
							<Box
								sx={{
									shadowColor: "#000",
									shadowOffset: {
										width: 0,
										height: 1,
									},
									shadowOpacity: 0.18,
									shadowRadius: 1.0,
									elevation: 1,
								}}
								marginBottom={8}
								bg="$basicwhite"
								paddingVertical={20}
								paddingHorizontal={8}
							>
								<HStack>
									<Box flex={1} justifyContent="center" alignItems="flex-start">
										{!isSignedIn && item.unitPrice && item.unitPrice > 3500 ? (
											<Button onPress={() => router.push("/contact")} gap={6} variant="primary" action="primary" width="$full">
												<BookCheck color="white" />
												<Typography color={"$basicwhite"} weight="bold">
													Enquire Now
												</Typography>
											</Button>
										) : (
											<AddToCart stockcode={item.stockcode} isPurchaseable={item.isPurchaseable} item={item} />
										)}
									</Box>
								</HStack>
							</Box>
						)}

						{isMobile && !item.isPurchaseable && (
							<Box flex={1} justifyContent="center" alignItems="flex-start">
								<Typography weight="normal">Please contact your supervisor to request this item.</Typography>
							</Box>
						)}
					</Box>

					<Box bg="$basicwhite" paddingHorizontal={isMobile ? 6 : 36} paddingVertical={36} borderRadius={12}>
						<TabContainer>
							<Tab title="Description" id="description">
								<H4>Description</H4>
								{item.description == null ? <Text>No description available</Text> : <RenderHtml source={{ html: `${item.description}` }} defaultTextProps={defaultTextProps} tagsStyles={tagsStyles} />}
							</Tab>
							{item.attributeCollection != null && item.attributeCollection?.length > 0 && (
								<Tab title="Specifications" id="specifications">
									<H4>Specifications</H4>
									<VStack space="md" maxWidth={500} mt={24}>
										{item.attributeCollection?.map((spec) => {
											return (
												<HStack key={spec.key} paddingHorizontal={12} borderBottomWidth={1} borderBottomColor="$neutral200" pb="$2" justifyContent="space-between">
													<Text>{spec.key}</Text>
													<Text fontWeight="bold">{spec.value}</Text>
												</HStack>
											);
										})}
									</VStack>
								</Tab>
							)}
							{/* <Tab title="Certifications" id="certifications">
								<Box gap={24} flexDirection="row" justifyContent="center" flexWrap="wrap">
									{[1, 2, 3].map((cert) => {
										return (
											<Box borderRadius={12} padding={24} bg="$neutral25" alignItems="center" maxWidth={355} width={"$1/3"} key={cert}>
												<BadgeCheck width={48} height={48} color={resolvedPrimarylight} />
												<H5>Certification Example</H5>
												<ParagraphSmall sx={{ textAlign: "center" }}>Lorem ipsum dolor sit amet consectetur euconvallis elita.</ParagraphSmall>
											</Box>
										);
									})}
								</Box>
							</Tab> */}
							{fileCollection.length > 0 && (
								<Tab title="Downloads" id="downloads">
									<VStack gap={24}>
										{fileCollection.map((download: any) => {
											return <ItemDownload key={download.url} title={download.type} url={download.url} />;
										})}
									</VStack>
								</Tab>
							)}
							{item.relatedItemCollection != null && item.relatedItemCollection.length > 0 && (
								<Tab title="Accessories" id="accessories">
									<AccessoriesTab item={item} />
								</Tab>
							)}
						</TabContainer>
					</Box>
				</Container>
			</Box>
		</ScrollContainer>
	);
}

function AccessoriesTab({ item }: { item: Item }) {
	const [relatedItems, setRelatedItems] = useState<Item[] | null>(null);
	const [findItem] = usePostItemFindMutation();

	useEffect(() => {
		if (!item.relatedItemCollection || item.relatedItemCollection.length === 0) {
			return;
		}

		const fetchRelatedItems = async () => {
			try {
				const result = await findItem({
					itemCriteria: {
						stockcodeCollection: item.relatedItemCollection,
						pageSize: 999,
					},
				}).unwrap();

				setRelatedItems(result?.list || []);
			} catch (error) {
				console.error("Failed to fetch related items:", error);
				setRelatedItems([]);
			}
		};

		fetchRelatedItems();
	}, [item.relatedItemCollection, findItem]);

	if (!item.relatedItemCollection || item.relatedItemCollection.length === 0) {
		return null;
	}

	if (relatedItems === null) {
		return (
			<Box minHeight={150}>
				<PageLoader />
			</Box>
		);
	}

	return (
		<>
			{relatedItems.map((relatedItem, index) => (
				<ItemListItem key={index} item={relatedItem} />
			))}
		</>
	);
}
